// hooks/useI18n.ts
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

// Translation data
const translations = {
  wordsOfAdvice: {
    en: [
      'Livy is an automated assistant, not a licensed healthcare professional. Always consult a qualified clinician before acting on advice.',
      "Don't share personal details that could identify you.",
    ],
    no: [
      'Livy er en automatisert assistent, ikke en lisensiert helsepersonell. Rådfør deg alltid med kvalifisert helsepersonell før du følger råd.',
      'Ikke del personlige opplysninger som kan identifisere deg.',
    ],
    de: [
      'Livy ist ein automatisierter Assistent und kein lizenzierter Gesundheitsdienstleister. Konsultieren Sie immer einen qualifizierten Arzt, bevor Sie auf Ratschläge reagieren.',
      'Geben Sie keine persönlichen Daten weiter, die Sie identifizieren könnten.',
    ],
    fr: [
      'Livy est un assistant automatisé, pas un professionnel de santé agréé. Consultez toujours un professionnel qualifié avant de suivre un conseil.',
      "Ne partagez pas d'informations personnelles permettant de vous identifier.",
    ],
  },

  labels: {
    en: {
      wordsOfAdviceTitle: 'Words of advice',
      audioAssistLabel: 'Enable audio assist',
      disclaimer: 'Check recommendations with your healthcare provider.',
      chatPlaceholder: 'Ask me anything ...',
      feedbackPlaceholder: '[Optional] Please provide an explanation.',
      audioGenerating: 'Generating audio, please wait...',
      audioFailed: 'Audio generation failed.',
      helloMessage: 'Hello, Livy is here.',
      questionPrompt:
        'Do you have question about health, nutrition, losing weight, motivation, or working out? Talk to me about anything.',
      // Sidebar translations
      chatHistory: 'Chat History',
      searchPlaceholder: 'Search conversations...',
      activeConversation: 'Active Conversation',
      recentChats: 'Recent Chats',
      olderChats: 'Older Chats',
      noResults: 'No results found',
      noHistory: 'No chat history available',
      startNewConversation: 'Start a new conversation to see it here',
      loadingHistory: 'Loading chat history...',
      loadMore: 'Load More',
      allLoaded: 'All conversations loaded',
      // Feedback translations
      likedResponse: 'You liked this response',
      dislikedResponse: 'You disliked this response',
      thanksForFeedback: 'Thanks for your feedback!',
      submitFeedback: 'Submit feedback',
      provideFeedback: 'Provide additional feedback (optional)',
      failedToSubmit: 'Failed to submit feedback',
      tryAgain: 'Try again',
      cancel: 'Cancel',
      thinking: 'is thinking',
      searching: 'is searching',
      analyzing: 'is analyzing',
      processing: 'is processing',
      typing: 'is typing',
      pauseAudio: 'Pause audio',
      generatePlayAudio: 'Generate and play audio',
    },
    no: {
      wordsOfAdviceTitle: 'Råd og veiledning',
      audioAssistLabel: 'Aktiver lydhjelp',
      disclaimer: 'Sjekk anbefalingene med helsepersonell.',
      chatPlaceholder: 'Spør meg om hva som helst ...',
      feedbackPlaceholder: '[Valgfritt] Gi en forklaring.',
      audioGenerating: 'Genererer lyd, vennligst vent ...',
      audioFailed: 'Lydgenerering mislyktes.',
      helloMessage: 'Hei, Livy er her.',
      questionPrompt:
        'Har du spørsmål om helse, ernæring, vekttap, motivasjon eller trening? Snakk med meg om hva som helst.',
      // Sidebar translations
      chatHistory: 'Samtalehistorikk',
      searchPlaceholder: 'Søk i samtaler...',
      activeConversation: 'Aktiv samtale',
      recentChats: 'Nylige samtaler',
      olderChats: 'Eldre samtaler',
      noResults: 'Ingen resultater funnet',
      noHistory: 'Ingen samtalehistorikk tilgjengelig',
      startNewConversation: 'Start en ny samtale for å se den her',
      loadingHistory: 'Laster samtalehistorikk...',
      loadMore: 'Last mer',
      allLoaded: 'Alle samtaler lastet',
      // Feedback translations
      likedResponse: 'Du likte dette svaret',
      dislikedResponse: 'Du likte ikke dette svaret',
      thanksForFeedback: 'Takk for tilbakemeldingen!',
      submitFeedback: 'Send tilbakemelding',
      provideFeedback: 'Gi tilleggskommentar (valgfritt)',
      failedToSubmit: 'Kunne ikke sende tilbakemelding',
      tryAgain: 'Prøv igjen',
      cancel: 'Avbryt',
    },
    de: {
      wordsOfAdviceTitle: 'Ratschläge',
      audioAssistLabel: 'Audiohilfe aktivieren',
      disclaimer: 'Besprechen Sie Empfehlungen mit Ihrem Arzt oder Ihrer Ärztin.',
      chatPlaceholder: 'Frag mich was ...',
      feedbackPlaceholder: '[Optional] Bitte geben Sie eine Erklärung ab.',
      audioGenerating: 'Audio wird generiert, bitte warten...',
      audioFailed: 'Die Audiogenerierung ist fehlgeschlagen.',
      helloMessage: 'Hallo, Livy ist hier.',
      questionPrompt:
        'Haben Sie Fragen zu Gesundheit, Ernährung, Gewichtsverlust, Motivation oder Training? Sprechen Sie mit mir über alles.',
      // Sidebar translations
      chatHistory: 'Chat-Verlauf',
      searchPlaceholder: 'Gespräche durchsuchen...',
      activeConversation: 'Aktives Gespräch',
      recentChats: 'Neueste Chats',
      olderChats: 'Ältere Chats',
      noResults: 'Keine Ergebnisse gefunden',
      noHistory: 'Kein Chat-Verlauf verfügbar',
      startNewConversation: 'Starten Sie ein neues Gespräch, um es hier zu sehen',
      loadingHistory: 'Chat-Verlauf wird geladen...',
      loadMore: 'Mehr laden',
      allLoaded: 'Alle Gespräche geladen',
      // Feedback translations
      likedResponse: 'Sie haben diese Antwort gemocht',
      dislikedResponse: 'Sie haben diese Antwort nicht gemocht',
      thanksForFeedback: 'Vielen Dank für Ihr Feedback!',
      submitFeedback: 'Feedback senden',
      provideFeedback: 'Zusätzliches Feedback geben (optional)',
      failedToSubmit: 'Feedback konnte nicht gesendet werden',
      tryAgain: 'Erneut versuchen',
      cancel: 'Abbrechen',
      thinking: 'denkt',
      searching: 'sucht',
      analyzing: 'analysiert',
      processing: 'verarbeitet',
      typing: 'tippt',
      pauseAudio: 'Audio pausieren',
      generatePlayAudio: 'Audio generieren und abspielen',
    },
    fr: {
      wordsOfAdviceTitle: 'Conseils',
      audioAssistLabel: "Activer l'assistance audio",
      disclaimer: 'Consultez votre professionnel de santé avant de suivre des recommandations.',
      chatPlaceholder: 'Posez-moi une question ...',
      feedbackPlaceholder: '[Facultatif] Veuillez fournir une explication.',
      audioGenerating: "Génération de l'audio, veuillez patienter...",
      audioFailed: "La génération de l'audio a échoué.",
      helloMessage: 'Bonjour, Livy est là.',
      questionPrompt:
        "Avez-vous des questions sur la santé, la nutrition, la perte de poids, la motivation ou l'exercice ? Parlez-moi de tout.",
      // Sidebar translations
      chatHistory: 'Historique des conversations',
      searchPlaceholder: 'Rechercher des conversations...',
      activeConversation: 'Conversation active',
      recentChats: 'Conversations récentes',
      olderChats: 'Anciennes conversations',
      noResults: 'Aucun résultat trouvé',
      noHistory: 'Aucun historique de conversation disponible',
      startNewConversation: 'Commencez une nouvelle conversation pour la voir ici',
      loadingHistory: "Chargement de l'historique des conversations...",
      loadMore: 'Charger plus',
      allLoaded: 'Toutes les conversations chargées',
      // Feedback translations
      likedResponse: 'Vous avez aimé cette réponse',
      dislikedResponse: "Vous n'avez pas aimé cette réponse",
      thanksForFeedback: 'Merci pour votre retour !',
      submitFeedback: 'Envoyer commentaire',
      provideFeedback: 'Fournir un commentaire supplémentaire (facultatif)',
      failedToSubmit: "Échec de l'envoi du commentaire",
      tryAgain: 'Réessayer',
      cancel: 'Annuler',
      thinking: 'réfléchit',
      searching: 'recherche',
      analyzing: 'analyse',
      processing: 'traite',
      typing: 'tape',
      pauseAudio: 'Mettre en pause',
      generatePlayAudio: "Générer et lire l'audio",
    },
  },

  citation: {
    en: {
      dialogTitle: 'Citation',
      source: 'Source',
      link: 'Link',
      content: 'Content',
      transparencyNotice:
        'When Livy shares health-related information, we include a citation to ensure transparency and accuracy. The excerpt is quoted directly from the original source and may appear in its original language.',
    },
    fr: {
      dialogTitle: 'Citation',
      source: 'Source',
      link: 'Lien',
      content: 'Contenu',
      transparencyNotice:
        "Lorsque Livy partage des informations liées à la santé, nous incluons une citation pour garantir la transparence et l'exactitude. L'extrait est cité directement à partir de la source originale et peut apparaître dans sa langue d'origine.",
    },
    de: {
      dialogTitle: 'Zitat',
      source: 'Quelle',
      link: 'Link',
      content: 'Inhalt',
      transparencyNotice:
        'Wenn Livy gesundheitsbezogene Informationen teilt, fügen wir eine Zitation hinzu, um Transparenz und Genauigkeit sicherzustellen. Das Zitat stammt direkt aus der Originalquelle und kann in der Originalsprache erscheinen.',
    },
    no: {
      dialogTitle: 'Sitat',
      source: 'Kilde',
      link: 'Lenke',
      content: 'Innhold',
      transparencyNotice:
        'Når Livy deler helse-relaterte informasjon, inkluderer vi en henvisning for å sikre åpenhet og nøyaktighet. Utdraget er sitert direkte fra den originale kilden og kan vises på sitt opprinnelige språk.',
    },
  },
};

type SupportedLocale = 'en' | 'no' | 'de' | 'fr';

export const useI18n = () => {
  const [searchParams] = useSearchParams();

  const locale = useMemo(() => {
    const paramLocale = searchParams.get('locale') || 'en';
    // Handle Norwegian variations
    if (paramLocale === 'nb') return 'no';
    // Ensure we have a supported locale
    return ['en', 'no', 'de', 'fr'].includes(paramLocale) ? (paramLocale as SupportedLocale) : 'en';
  }, [searchParams]);

  const t = useMemo(() => {
    return {
      // Get words of advice text
      getWordsOfAdvice: () => translations.wordsOfAdvice[locale] || translations.wordsOfAdvice.en,

      // Get label translations
      get: (key: string) => {
        return (translations.labels[locale] as Record<string, string>)[key] || key;
      },

      // Get citation translations
      getCitation: (key: string) => {
        return (translations.citation[locale] as Record<string, string>)[key] || key;
      },

      // Get current locale
      locale,

      // Check if current locale is RTL (none of the supported languages are RTL)
      isRTL: false,

      // Get user language name for backend
      getUserLanguage: () => {
        switch (locale) {
          case 'no':
            return 'Norwegian';
          case 'de':
            return 'German';
          case 'fr':
            return 'French';
          default:
            return 'English';
        }
      },
    };
  }, [locale]);

  return t;
};

export default useI18n;
